import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { valibotResolver } from '@hookform/resolvers/valibot'
import type { LoanFormData } from '../../lib/loan-schema'
import { LoanFormSchema } from '../../lib/loan-schema'
import { content } from '../lib/content'

/**
 * Loan application form with validation and success/error states.
 * Uses shared Valibot schema for client and server validation.
 */
export function LoanApplicationForm() {
  const [loading, setLoading] = useState(false)
  const [complete, setComplete] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoanFormData>({
    resolver: valibotResolver(LoanFormSchema),
  })

  const onSubmit = async (data: LoanFormData) => {
    setSubmitError(null)
    setLoading(true)
    try {
      const res = await fetch('/api/loan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      const json = await res.json()
      if (!res.ok) {
        const msg = json?.errors?._form ?? json?.message ?? content.errorMessage
        setSubmitError(msg)
        return
      }
      if (!json?.id && !json?.success) {
        setSubmitError(content.errorMessage)
        return
      }
      setComplete(true)
    } catch {
      setSubmitError(content.errorMessage)
    } finally {
      setLoading(false)
    }
  }

  if (complete) {
    return (
      <div className='rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
        <p className='text-lg text-gray-700'>{content.successMessage}</p>
      </div>
    )
  }

  return (
    <div className='rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
      <h2 className='mb-4 text-lg font-semibold text-gray-900'>{content.applyHeading}</h2>
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
        <div className='grid gap-4 sm:grid-cols-2'>
          <Input
            label={content.formLabels.employeeFirstName}
            error={errors.employeeFirstName?.message}
            register={register}
            name='employeeFirstName'
          />
          <Input
            label={content.formLabels.employeeLastName}
            error={errors.employeeLastName?.message}
            register={register}
            name='employeeLastName'
          />
        </div>

        <Input
          label={content.formLabels.employeeEmail}
          error={errors.employeeEmail?.message}
          register={register}
          name='employeeEmail'
          type='email'
        />

        <Input
          label={content.formLabels.employeeNumber}
          error={errors.employeeNumber?.message}
          register={register}
          name='employeeNumber'
        />

        <Input
          label={content.formLabels.streetAddress}
          error={errors.streetAddress?.message}
          register={register}
          name='streetAddress'
        />

        <div className='grid gap-4 sm:grid-cols-2'>
          <Input
            label={content.formLabels.city}
            error={errors.city?.message}
            register={register}
            name='city'
          />
          <Input
            label={content.formLabels.postalCode}
            error={errors.postalCode?.message}
            register={register}
            name='postalCode'
            placeholder='A1A 1A1'
          />
        </div>

        <Input
          label={content.formLabels.phoneNumber}
          error={errors.phoneNumber?.message}
          register={register}
          name='phoneNumber'
          type='tel'
        />

        <ul className='list-disc space-y-2 pl-5 text-sm text-gray-500'>
          <li>Loan amount: $1,000</li>
          <li>Interest rate: 3% annual</li>
          <li>Payment schedule: 30 days after loan is issued</li>
          <li>Repayment: $40 per paycheque</li>
        </ul>

        {submitError && <p className='text-sm text-red-600'>{submitError}</p>}

        {loading ? (
          <div className='flex items-center gap-2'>
            <span className='inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600' />
            <span className='text-sm text-gray-500'>Submitting…</span>
          </div>
        ) : (
          <button
            type='submit'
            className='rounded-md bg-[#5693cc] px-5 py-3 text-sm font-medium text-white hover:bg-[#102f49] focus:outline-none focus:ring-2 focus:ring-[#102f49] focus:ring-offset-2'
          >
            {content.submitButton}
          </button>
        )}
      </form>
    </div>
  )
}

type InputProps = {
  label: string
  error?: string
  register: ReturnType<typeof useForm<LoanFormData>>['register']
  name: keyof LoanFormData
  placeholder?: string
  type?: string
}

const Input = ({ label, error, register, name, placeholder, type = 'text' }: InputProps) => (
  <div>
    <label className='mb-1 block text-sm font-medium text-gray-700'>
      {label} <span className='text-red-500'>*</span>
    </label>
    <input
      {...register(name)}
      type={type}
      placeholder={placeholder}
      className='block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 shadow-sm ring-offset-2 focus:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#102f49]'
    />
    {error && <p className='mt-1 text-sm text-red-600'>{error}</p>}
  </div>
)
